/**
 * RoleDesignerView — editor de un Role Template (OT-0055, antes visor
 * B.6.d.9.j).
 *
 * Comparte con los demás editores la cabecera (metadata, metodología,
 * autosave, acciones: clonar / publicar / nueva versión / archivar) y el
 * banner de solo lectura, que monta `TemplateDetailView`.
 *
 * Edita, por idioma, nombre, descripción y responsabilidades; y una vez
 * para todos los idiomas, el ámbito (`applicable_scopes`): equipo interno
 * y/o cliente. Un rol de cliente convierte en firma del cliente la etapa
 * de aprobación que lo pide.
 *
 * Solo lectura si es del producto, si la versión está publicada o si el
 * usuario no es admin (misma regla que el resto de editores).
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, UserRound } from 'lucide-react'

import type {
  RoleDefinition,
  RoleI18nBlock,
  RoleScope,
  Template,
  TemplateVersionWithDefinition,
} from '../../../api/templates'
import { ListInput } from './ListInput'
import type { ClientValidationIssue } from './state'
import { useDefinitionAutosave, type AutosaveState } from './useDefinitionAutosave'
import { editingLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../../../i18n/config'
import type { TenantManageBlock } from '../../../lib/tiers'

type Locale = SupportedLocale
const SCOPES: RoleScope[] = ['internal', 'client_stakeholder']
/** Espejo de `RoleI18nBlock.responsibilities` (`max_length=20`). */
const MAX_RESPONSIBILITIES = 20

interface RoleDesignerViewProps {
  template: Template
  version: TemplateVersionWithDefinition
  manageBlock: TenantManageBlock | null
  renderHeader: (autosave: AutosaveState) => React.ReactNode
  renderReadOnlyBanner: () => React.ReactNode
}

export function RoleDesignerView({
  template,
  version,
  manageBlock,
  renderHeader,
  renderReadOnlyBanner,
}: RoleDesignerViewProps) {
  const { t, i18n } = useTranslation('templates')
  const readOnly =
    template.is_system_default || version.published_at !== null || manageBlock !== null

  const savedDefinition = version.definition as unknown as RoleDefinition
  const [definition, setDefinition] = useState<RoleDefinition>(savedDefinition)
  const locales = SUPPORTED_LOCALES.filter((loc) => definition.i18n[loc])
  const [activeLocale, setActiveLocale] = useState<Locale>(() =>
    editingLocale(locales, i18n.language),
  )

  const autosave = useDefinitionAutosave({
    templateId: template.id,
    versionId: version.id,
    definition,
    savedDefinition,
    readOnly,
    preflight: findRoleIssues,
  })

  const block = definition.i18n[activeLocale]
  const missingLocales = SUPPORTED_LOCALES.filter((loc) => !definition.i18n[loc])
  const scopes = definition.applicable_scopes ?? null

  const updateBlock = (patch: Partial<RoleI18nBlock>) =>
    setDefinition((d) => ({
      ...d,
      i18n: { ...d.i18n, [activeLocale]: { ...d.i18n[activeLocale], ...patch } as RoleI18nBlock },
    }))
  const addLocale = (loc: Locale) => {
    // Arranca con el nombre del idioma vigente para que la versión siga
    // siendo válida; el autor lo traduce.
    setDefinition((d) => ({
      ...d,
      i18n: { ...d.i18n, [loc]: { name: d.i18n[activeLocale]?.name ?? '' } },
    }))
    setActiveLocale(loc)
  }
  const toggleScope = (scope: RoleScope) =>
    setDefinition((d) => {
      // Sin ámbito declarado cuenta como interno: se parte de ahí.
      const current = d.applicable_scopes ?? ['internal']
      const next = current.includes(scope)
        ? current.filter((s) => s !== scope)
        : SCOPES.filter((s) => s === scope || current.includes(s))
      return { ...d, applicable_scopes: next }
    })

  return (
    <div className="h-full flex flex-col min-h-0">
      {renderHeader(autosave)}
      {readOnly ? renderReadOnlyBanner() : null}

      {/* Idioma en edición */}
      <div className="bg-surface border-b border-line px-6 py-2 flex items-center justify-end gap-1.5">
        <span className="text-[10px] uppercase tracking-wide text-ink-subtle">
          {t('designer.global_config.editing_locale')}
        </span>
        {locales.map((loc) => (
          <button
            key={loc}
            type="button"
            onClick={() => setActiveLocale(loc)}
            aria-pressed={loc === activeLocale}
            className={
              loc === activeLocale
                ? 'px-2 py-0.5 text-xs font-mono uppercase rounded bg-brand-soft text-brand'
                : 'px-2 py-0.5 text-xs font-mono uppercase rounded hover:bg-surface-muted text-ink-muted'
            }
          >
            {loc}
          </button>
        ))}
        {!readOnly
          ? missingLocales.map((loc) => (
              <button
                key={loc}
                type="button"
                onClick={() => addLocale(loc)}
                title={t('designer.global_config.add_locale')}
                className="px-2 py-0.5 text-xs font-mono uppercase rounded border border-dashed border-line text-ink-subtle hover:text-ink hover:border-line-strong inline-flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                {loc}
              </button>
            ))
          : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-canvas">
        <div className="max-w-3xl mx-auto py-8 px-6 space-y-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-subtle">
            <UserRound className="w-3.5 h-3.5" />
            <span>{t('designer.role.eyebrow')}</span>
          </div>

          {!block ? (
            <p className="text-center text-sm text-ink-subtle italic">
              {t('designer.role.no_locale_content')}
            </p>
          ) : (
            <section className="space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-ink-muted">
                  {t('designer.role.name_label')}
                </span>
                <input
                  type="text"
                  value={block.name}
                  onChange={(e) => updateBlock({ name: e.target.value })}
                  disabled={readOnly}
                  maxLength={255}
                  lang={activeLocale}
                  className="mt-1 w-full bg-surface border border-line rounded-md px-3 py-2 font-display text-lg text-ink focus:outline-none focus:border-brand disabled:opacity-70"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-ink-muted">
                  {t('designer.role.description_label')}
                </span>
                <textarea
                  value={block.description ?? ''}
                  onChange={(e) => updateBlock({ description: e.target.value })}
                  disabled={readOnly}
                  maxLength={2000}
                  rows={3}
                  lang={activeLocale}
                  className="mt-1 w-full bg-surface border border-line rounded-md px-3 py-2 font-body text-sm text-ink focus:outline-none focus:border-brand disabled:opacity-70 resize-y"
                />
              </label>
              <div>
                <p className="text-xs font-medium text-ink-muted">
                  {t('designer.role.responsibilities_title')}
                </p>
                <p className="text-[11px] text-ink-subtle mb-2">
                  {t('designer.role.responsibilities_hint')}
                </p>
                <ListInput
                  items={block.responsibilities ?? []}
                  onChange={(responsibilities) => updateBlock({ responsibilities })}
                  placeholder={t('designer.role.responsibilities_placeholder')}
                  addLabel={t('designer.role.responsibilities_add')}
                  maxItems={MAX_RESPONSIBILITIES}
                  readOnly={readOnly}
                />
              </div>
            </section>
          )}

          <fieldset className="border-t border-line pt-6">
            <legend className="float-left w-full text-xs font-medium text-ink-muted">
              {t('designer.role.scope_title')}
            </legend>
            <p className="clear-left text-[11px] text-ink-subtle mb-3">
              {t('designer.role.scope_hint')}
            </p>
            <div className="space-y-2">
              {SCOPES.map((scope) => {
                const key = scope === 'internal' ? 'internal' : 'client'
                return (
                  <label key={scope} className="flex items-start gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={(scopes ?? ['internal']).includes(scope)}
                      onChange={() => toggleScope(scope)}
                      disabled={readOnly}
                      className="mt-1 accent-brand"
                    />
                    <span>
                      {t(`designer.role.scope_${key}`)}
                      <span className="block text-[11px] text-ink-subtle">
                        {t(`designer.role.scope_${key}_hint`)}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
            {scopes === null ? (
              <p className="mt-2 text-[11px] text-ink-subtle italic">
                {t('designer.role.scope_legacy')}
              </p>
            ) : null}
          </fieldset>
        </div>
      </div>
    </div>
  )
}

/** Lo que el backend rechazaría (`RoleDefinition`): un idioma sin nombre o
 * un ámbito vacío. */
function findRoleIssues(definition: RoleDefinition): ClientValidationIssue[] {
  const issues: ClientValidationIssue[] = []
  for (const loc of SUPPORTED_LOCALES) {
    const block = definition.i18n[loc]
    if (block && !block.name.trim()) {
      issues.push({ code: 'missing_name', path: `i18n.${loc}.name`, params: { locale: loc } })
    }
  }
  if (definition.applicable_scopes?.length === 0) {
    issues.push({ code: 'missing_scope', path: 'applicable_scopes', params: {} })
  }
  return issues
}
